<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import { dateFormatLocalized } from 'shared/helpers/timeHelper';
import { inboxDisplayName } from 'dashboard/helper/inboxDisplayName';
import { useContactDetailI18n } from './useContactDetailI18n';
import { contactConnectionOptions } from './contactConnectionOptions';

const props = defineProps({ identity: { type: Object, required: true } });
const emit = defineEmits(['openConversation']);
const { t } = useContactDetailI18n();
const route = useRoute();
const store = useStore();
const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const flags = useMapGetter('contactConversations/getUIFlags');
const relatedContactIds = computed(() => [
  ...new Set(
    (props.identity.connections || []).map(
      connection => connection.contact_id || route.params.contactId
    )
  ),
]);
watch(
  relatedContactIds,
  ids => {
    ids
      .filter(id => Number(id) !== Number(route.params.contactId))
      .forEach(id => {
        store.dispatch('contactConversations/get', id).catch(() => null);
      });
  },
  { immediate: true }
);
const options = computed(() =>
  (props.identity.connections || [])
    .flatMap(connection =>
      contactConnectionOptions(
        { ...props.identity, connections: [connection] },
        conversations.value(connection.contact_id || route.params.contactId)
      )
    )
    .sort((a, b) => b.lastMessageAt - a.lastMessageAt)
);
const hasConversation = computed(() =>
  options.value.some(option => option.conversation)
);
const showMenu = ref(false);
const select = option => {
  showMenu.value = false;
  emit('openConversation', {
    ...props.identity,
    connections: [option.connection],
    conversation: option.conversation,
  });
};
const open = () => {
  if (options.value.length === 1) select(options.value[0]);
  else showMenu.value = !showMenu.value;
};
const description = option => {
  if (!option.conversation) return t('CONTACT_DETAIL.NO_CONVERSATION_YET');
  if (!option.lastMessageAt) return t('CONTACT_DETAIL.NO_MESSAGES_YET');
  return t('CONTACT_DETAIL.CONNECTION_LAST_MESSAGE', {
    date: dateFormatLocalized(option.lastMessageAt, "dd/MM/yyyy 'às' HH:mm"),
  });
};
</script>

<template>
  <div
    v-on-click-outside="() => (showMenu = false)"
    class="relative ml-auto shrink-0 max-[600px]:col-start-2 max-[600px]:ml-0"
    @keydown.esc="showMenu = false"
  >
    <button
      type="button"
      class="inline-flex h-[38px] items-center justify-center gap-2 rounded-[10px] bg-[#16A34A] px-3 text-[13px] font-semibold text-white hover:bg-[#15803D] disabled:opacity-50"
      :disabled="!options.length || flags.isFetching || flags.isCreating"
      :aria-expanded="options.length > 1 ? showMenu : undefined"
      :aria-haspopup="options.length > 1 ? 'menu' : undefined"
      @click="open"
    >
      <span class="i-ri-whatsapp-line size-4 shrink-0" />
      {{
        hasConversation
          ? t('CONTACT_DETAIL_VIEW.OPEN_CONVERSATION')
          : t('CONTACT_DETAIL.START_CONVERSATION')
      }}
      <span
        v-if="options.length > 1"
        class="i-lucide-chevron-down size-3.5 shrink-0"
      />
    </button>
    <div
      v-if="showMenu"
      role="menu"
      class="absolute right-0 top-full z-30 mt-2 w-[340px] max-w-[calc(100vw-48px)] rounded-[14px] border border-n-weak bg-white p-1.5 shadow-lg max-[600px]:left-0 max-[600px]:right-auto"
    >
      <p class="m-0 px-3 py-2 text-[11px] font-medium uppercase text-[#8C959F]">
        {{ t('CONTACT_DETAIL.CHOOSE_CONNECTION') }}
      </p>
      <button
        v-for="(option, index) in options"
        :key="option.connection.inbox_id"
        type="button"
        role="menuitem"
        class="flex w-full items-start gap-2 rounded-[10px] px-3 py-2.5 text-left hover:bg-[#F5F5F3]"
        @click="select(option)"
      >
        <span class="mt-1.5 size-2 shrink-0 rounded-full bg-[#16A34A]" />
        <span class="flex min-w-0 flex-1 flex-col gap-1">
          <span
            class="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-[#101828]"
          >
            <span class="break-words">{{
              inboxDisplayName(option.connection.inbox_name)
            }}</span>
            <span
              v-if="index === 0 && option.lastMessageAt"
              class="rounded-full bg-[#EAF7EE] px-1.5 py-0.5 text-[10px] font-medium text-[#15803D]"
              >{{ t('CONTACT_DETAIL.MOST_RECENT') }}</span
            >
          </span>
          <span class="text-xs text-[#6B7280]">{{ description(option) }}</span>
        </span>
      </button>
    </div>
  </div>
</template>
